/* cf-real-line-items.js - Click Fencing, 2026-08-15 (delivery fix 2026-08-18)
 *
 * Until now every calculator order checked out as ONE line: the UNLISTED "Quote Total"
 * product (variant 42909527670878, $0.01) with quantity set to the grand total in cents.
 * A $707.72 quote became "Quote Total x 70772". The money was right - the customer paid
 * exactly what was quoted - but the order carried no SKUs, so there was no picking list,
 * inventory never moved, and product reporting was blind.
 *
 * This asset replaces that single line with the real products, without touching
 * assets/quote-review.js (128KB) or either 700KB calculator. It intercepts the
 * /cart/add.js the checkout handler makes, and only when the payload is exactly the
 * placeholder line - anything else passes straight through.
 *
 * THE RULE THIS IS BUILT AROUND: the customer must never be charged more than the quote.
 * After posting the real lines the cart is read back and reconciled against the quote:
 *
 *   short  -> add the $0.01 placeholder for the balance, so lines that have no Shopify
 *             product are still paid for
 *   over   -> abandon the whole approach, clear, and post the original placeholder line.
 *             Charging the quoted total with no SKUs beats overcharging with them.
 *   failed -> same fallback. Checkout never breaks because of this asset.
 *
 * Self-installing, idempotent, every path wrapped. Load AFTER cf-sku-prices.js (needs
 * CF_VARIANT) and after cf-quote-editor.js.
 */
(function () {
  'use strict';
  if (window.__cfRealLinesInstalled) return;
  window.__cfRealLinesInstalled = true;

  var PLACEHOLDER_VARIANT = 42909527670878;
  var CALCS = ['ullc', 'blade', 'flattop', 'perfmesh'];

  /* WHICH quote is on screen. This decides what the customer is charged, so it is not
     allowed to guess. Note <calc>_active_quote holds the active quote NUMBER (blade
     stores "9"), not the quote object, so it has to be matched back against
     <calc>_quotes. */
  function candidates() {
    var out = [];
    for (var i = 0; i < CALCS.length; i++) {
      var c = CALCS[i], active = null;
      try { active = JSON.parse(localStorage.getItem(c + '_active_quote') || 'null'); } catch (e) {}
      if (active && active.breakdown && active.breakdown.length) {
        out.push({ quote: active, calc: c, isActive: true });
      }
      var activeNum = (active !== null && typeof active !== 'object') ? Number(active) : null;
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(c + '_quotes') || '[]') || []; } catch (e) {}
      if (Object.prototype.toString.call(arr) === '[object Array]') {
        for (var j = 0; j < arr.length; j++) {
          var q = arr[j];
          if (!q || !q.breakdown || !q.breakdown.length) continue;
          out.push({ quote: q, calc: c, isActive: activeNum !== null && Number(q.quoteNum) === activeNum });
        }
      }
    }
    return out;
  }

  function readQuote() {
    /* MIXED FENCE JOB (cf-job.js, 2026-09-23). The lookup below picks ONE saved quote by
       the active pill's label; a job has no single such quote, so it would charge one
       fence of a two-fence order. CF_JOB.merge() returns every run's lines in one quote,
       already ex-delivery and with a single GST line — which isNonStock()/isTax()/
       isDelivery() below then handle exactly as they do for a glass quote. */
    try {
      if (window.CF_JOB && window.CF_JOB.isOn()) {
        var job = window.CF_JOB.merge();
        if (job && job.breakdown && job.breakdown.length) return job;
      }
    } catch (e) {}
    /* The quote quote-review actually rendered, published by hydrate(). This is the
       authority: the pill-text match below silently stopped working when a "×" was
       appended to the pill, and its fallback can pick another calculator's quote. */
    try {
      var vq = window.__cfViewedQuote;
      if (vq && vq.breakdown && vq.breakdown.length) return vq;
    } catch (e) {}
    var list = candidates();
    if (!list.length) return null;
    var pill = document.querySelector('.qr-quote-pill.is-active');
    var label = pill ? String(pill.textContent || '').trim() : '';
    if (label) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].quote.quoteName || '').trim() === label) return list[i].quote;
      }
    }
    var pool = list.filter(function (r) { return r.isActive; });
    if (!pool.length) pool = list;
    pool.sort(function (a, b) {
      return (Date.parse(b.quote.generatedAt) || 0) - (Date.parse(a.quote.generatedAt) || 0);
    });
    return pool[0].quote;
  }

  function isNonStock(b) {
    return /delivery|pickup|inspection fee/i.test(String(b.category || '') + ' ' + String(b.item || ''));
  }

  /* The GLASS calculator puts a "GST (10%)" line in its own breakdown, so its grandTotal
     is GST-INCLUSIVE. The three aluminium calculators do not. The store runs
     taxesIncluded = false, so Shopify adds GST to whatever the cart holds - putting
     glass's GST line into the cart charges GST twice. Tax lines are therefore excluded
     from the cart AND subtracted from the reconciliation target. */
  function isTax(b) {
    return /^tax$/i.test(String(b.category || '').trim()) || /\bgst\b/i.test(String(b.item || ''));
  }

  function taxCentsOf(q) {
    var t = 0;
    q.breakdown.forEach(function (b) { if (isTax(b)) t += Math.round((Number(b.totalPrice) || 0) * 100); });
    return t;
  }

  /* DELIVERY, added 2026-08-18. Exactly the same trap as GST, found the same way.
     The calculators add their own delivery line:

         const deliveryCharge = this.shopifyData.settings?.deliveryCharge ?? 90;
         if (this.state.includeDelivery) { total += deliveryCharge; ... }

     so grandTotal already contains the $90. isNonStock() correctly keeps that line OUT
     of the real cart lines - but the target was still the full grandTotal, so the cart
     came up $90 short and the balance line silently added it back. Shopify then applied
     its own $90 "Perth metro delivery" rate on top: $180 of delivery on a $90 job.

     Shopify owns shipping now (the $90 metro rate, or $0 if the customer picks up at
     Canning Vale), so the quote's delivery amount is display-only and must come out of
     the reconciliation target too. Pick-up lines are $0, so this is a no-op for them. */
  function isDelivery(b) {
    return /^delivery$/i.test(String(b.category || '').trim());
  }

  function deliveryCentsOf(q) {
    var t = 0;
    q.breakdown.forEach(function (b) { if (isDelivery(b)) t += Math.round((Number(b.totalPrice) || 0) * 100); });
    return t;
  }

  /* breakdown.sku holds a real James Glen SKU on some lines ("49838") and a variant id on
     others ("42789310890078"), so try variantId, then a bare id, then the resolver. */
  function resolveVariant(b) {
    if (b.variantId) return Number(b.variantId) || null;
    var sku = b.sku == null ? '' : String(b.sku);
    if (/^\d{12,}$/.test(sku)) return Number(sku) || null;
    if (sku && typeof window.CF_VARIANT === 'function') {
      try {
        var v = window.CF_VARIANT(sku);
        if (v) return Number(v) || null;
      } catch (e) {}
    }
    return null;
  }

  function buildItems(q, adj) {
    var order = [], byVariant = {}, unresolved = [];
    function lineKey(x) {
      return String(x.item || '') + '|' + String(x.sku || '') + '|' + String(x.section || '');
    }
    var adjByLine = {};
    (adj || []).forEach(function (a) { adjByLine[lineKey(a)] = a; });
    var removedNote = (adj || []).filter(function (a) { return a.action === 'removed'; })
      .map(function (a) { return a.from + ' x ' + a.item; }).join('; ');

    q.breakdown.forEach(function (b) {
      var qty = Math.max(0, Math.floor(Number(b.quantity) || 0));
      if (!qty || isNonStock(b) || isTax(b)) return;
      var unit = Number(b.unitPrice) || 0;
      var vid = resolveVariant(b);
      if (!vid) {
        unresolved.push({ item: b.item, sku: b.sku, quantity: qty, value: Math.round(qty * unit * 100) });
        return;
      }
      var key = String(vid);
      if (!byVariant[key]) {
        byVariant[key] = { id: vid, quantity: 0, sections: [], properties: {}, adj: null };
        order.push(key);
      }
      var e = byVariant[key];
      e.quantity += qty;
      if (b.section && e.sections.indexOf(String(b.section)) < 0) e.sections.push(String(b.section));
      if (b.colour && !e.properties['Colour']) e.properties['Colour'] = String(b.colour);
      if (!e.adj && adjByLine[lineKey(b)]) e.adj = adjByLine[lineKey(b)];
    });

    var items = order.map(function (key, i) {
      var e = byVariant[key];
      var props = e.properties;
      if (e.sections.length) props['Section'] = e.sections.join(', ');
      var a = e.adj;
      if (a) props['Adjusted by customer'] = a.from + ' to ' + a.to;
      if (i === 0 && removedNote) props['Customer removed from quote'] = removedNote;
      return { id: e.id, quantity: e.quantity, properties: props };
    });

    return { items: items, unresolved: unresolved };
  }

  /* THE DESIGN & KIT LADDER - 2026-09-12. The gap between the quote (calculator prices,
     held by cf-calc-price-lock.js) and the real products (store prices) is charged as
     "Custom design, cut plan & kit check" - Pete's decision: the customer sees what the
     extra is for. Product 7766705045598, one variant per amount, so every line is qty 1
     instead of the old placeholder's "x 4512" cents. Greedy largest-first; the 1c step
     means any amount ties out exactly. Falls back to the placeholder if it ever cannot. */
  var DESIGN_LADDER = [
    [200000, 43316190183518], [100000, 43316190150750], [90000, 43316190117982],
    [80000, 43316190085214], [70000, 43316190052446], [60000, 43316190019678],
    [50000, 43316189986910], [40000, 43316189954142], [30000, 43316189921374],
    [20000, 43316189888606], [10000, 43316189855838], [9000, 43316189823070],
    [8000, 43316189790302], [7000, 43316189757534], [6000, 43316189724766],
    [5000, 43316189691998], [4000, 43316189659230], [3000, 43316189626462],
    [2000, 43316189593694], [1000, 43316188905566], [900, 43316188872798],
    [800, 43316188840030], [700, 43316188807262], [600, 43316188774494],
    [500, 43316188741726], [400, 43316188708958], [300, 43316188676190],
    [200, 43316188643422], [100, 43316188119134], [90, 43316191690846],
    [80, 43316191658078], [70, 43316191625310], [60, 43316191592542],
    [50, 43316191559774], [40, 43316191527006], [30, 43316191494238],
    [20, 43316191461470], [10, 43316191428702], [9, 43316191985758],
    [8, 43316191952990], [7, 43316191920222], [6, 43316191887454],
    [5, 43316191854686], [4, 43316191821918], [3, 43316191789150],
    [2, 43316191756382], [1, 43316191723614]
  ];

  function designItems(cents, note, summaryProps) {
    var items = [], rest = Math.round(Number(cents) || 0);
    if (rest <= 0) return null;
    for (var i = 0; i < DESIGN_LADDER.length && rest > 0; i++) {
      var step = DESIGN_LADDER[i][0];
      if (step > rest) continue;
      var qty = Math.floor(rest / step);
      rest -= qty * step;
      items.push({
        id: DESIGN_LADDER[i][1], quantity: qty,
        properties: items.length ? {} : Object.assign({}, summaryProps, { 'Note': note })
      });
    }
    return rest === 0 && items.length ? items : null;
  }

  function installHook() {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;

    function raw(url, init) { return origFetch.call(window, url, init); }

    function postJson(url, obj) {
      return raw(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(obj)
      });
    }

    function fallback(input, init, why) {
      console.warn('[cf-real-line-items] falling back to quote-total line: ' + why);
      return raw('/cart/clear.js', { method: 'POST' })
        .catch(function () {})
        .then(function () { return raw(input, init); });
    }

    function replaceLines(input, init, payload) {
      var summaryProps = (payload.items[0] && payload.items[0].properties) || {};

      var q = readQuote();
      var st = window.__cfQuoteState;
      var adj = null;
      if (st && st.quote && q && String(st.quote.quoteName || '') === String(q.quoteName || '')) {
        q = st.quote;
        adj = st.adj;
      }

      if (!q || !q.breakdown || !q.breakdown.length) return fallback(input, init, 'no saved quote found');

      var built = buildItems(q, adj);
      if (!built.items.length) return fallback(input, init, 'nothing resolved to a variant');

      built.items[0].properties = Object.assign({}, summaryProps, built.items[0].properties);

      /* Reconcile against the EX-GST, EX-DELIVERY total, so Shopify's own tax and
         shipping are the only ones applied. */
      var targetCents = Math.round((Number(q.grandTotal) || 0) * 100)
                        - taxCentsOf(q)
                        - deliveryCentsOf(q);

      return postJson('/cart/add.js', { items: built.items })
        .then(function (res) {
          if (!res.ok) throw new Error('cart rejected real lines (' + res.status + ')');
          return raw('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          var actual = Number(cart.total_price) || 0;
          var diff = targetCents - actual;

          if (diff === 0) {
            console.log('[cf-real-line-items] ' + built.items.length + ' lines, total ties out at ' + targetCents + 'c');
            return new Response(JSON.stringify({ items: cart.items }), {
              status: 200, headers: { 'Content-Type': 'application/json' }
            });
          }

          if (diff < 0) {
            throw new Error('real lines exceed quote by ' + (-diff) + 'c');
          }

          var note = built.unresolved.length
            ? 'Balance of quoted total - ' + built.unresolved.map(function (u) {
                return u.quantity + ' x ' + u.item;
              }).join('; ')
            : 'Custom design, cut plan & kit check';   // 2026-09-12: the calculator price-lock gap (cf-calc-price-lock.js)

          var designed = designItems(diff, note, summaryProps);
          return postJson('/cart/add.js', designed ? { items: designed } : {
            items: [{
              id: PLACEHOLDER_VARIANT,
              quantity: diff,
              properties: Object.assign({}, summaryProps, { 'Note': note })
            }]
          }).then(function (res2) {
            if (!res2.ok) throw new Error('balance line rejected (' + res2.status + ')');
            console.log('[cf-real-line-items] ' + built.items.length + ' lines + ' + diff + 'c balance ('
              + built.unresolved.length + ' unresolved)');
            return res2;
          });
        })
        .catch(function (e) {
          return fallback(input, init, e && e.message ? e.message : String(e));
        });
    }

    window.fetch = function (input, init) {
      try {
        var url = String((input && input.url) || input || '');
        if (/\/cart\/add(\.js)?/.test(url) && init && init.body) {
          var payload = JSON.parse(String(init.body));
          if (payload && payload.items && payload.items.length === 1
              && Number(payload.items[0].id) === PLACEHOLDER_VARIANT
              && Number(payload.items[0].quantity) > 1) {
            return replaceLines(input, init, payload);
          }
        }
      } catch (e) {
        console.warn('[cf-real-line-items] passthrough', e);
      }
      return origFetch.apply(this, arguments);
    };

    console.log('[cf-real-line-items] cart hook installed');
  }

  try { installHook(); } catch (e) { console.warn('[cf-real-line-items] install failed', e); }
})();
